// Localização: frontend/src/components/Financeiro/ListaContasBancarias.jsx

import React from 'react';
import { useSearch } from '../../context/SearchContext';
import PrimaryButton from '../Common/PrimaryButton';
import { Wallet, SquarePen, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

// Componente de Card para Mobile
const ContaCard = ({ conta, canEdit, canDelete, onEdit, onDelete }) => {
    return (
        <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-400 group">
            <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100 p-2 sm:p-4">
                <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-emerald-500 flex items-center justify-center shadow-md">
                            <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {conta.nome}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
                            <span className="font-semibold">{conta.banco}</span> • {conta.tipo_conta}
                        </p>
                        {conta.agencia && conta.conta && (
                            <p className="text-xs text-gray-500 mt-0.5">
                                Ag: {conta.agencia} • CC: {conta.conta}
                            </p>
                        )}
                    </div>
                    <div className="flex-shrink-0">
                        {conta.ativo ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                        )}
                    </div>
                </div>
            </div>

            <div className="p-2 sm:p-4 space-y-2 sm:space-y-3">
                <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-gray-600">Saldo Atual:</span>
                    <span className="text-sm sm:text-base font-bold text-emerald-600">
                        {formatCurrency(conta.saldo_atual)}
                    </span>
                </div>

                {conta.observacoes && (
                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                        {conta.observacoes}
                    </p>
                )}

                <div className="flex items-center justify-end pt-2 sm:pt-3 border-t border-gray-200 space-x-2">
                    {canEdit && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(conta);
                            }}
                            className="p-1.5 sm:p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors duration-200"
                            title="Editar"
                        >
                            <SquarePen className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(conta.id);
                            }}
                            className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title="Desativar"
                        >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const ListaContasBancarias = ({
    data,
    canCreate,
    canEdit,
    canDelete,
    handleOpenCreateModal,
    handleOpenEditModal,
    handleDelete
}) => {
    const { searchTerm } = useSearch();

    // Filtrar dados baseado na busca
    const filteredData = React.useMemo(() => {
        if (!searchTerm) return data;
        const lowerCaseSearch = searchTerm.toLowerCase();
        return data.filter(conta =>
            conta.nome?.toLowerCase().includes(lowerCaseSearch) ||
            conta.banco?.toLowerCase().includes(lowerCaseSearch) ||
            conta.conta?.toLowerCase().includes(lowerCaseSearch) ||
            conta.agencia?.toLowerCase().includes(lowerCaseSearch)
        );
    }, [data, searchTerm]);

    return (
        <div className="mt-4">
            <div className="flex justify-end items-center mb-4">
                {canCreate && (
                    <PrimaryButton
                        onClick={handleOpenCreateModal}
                        className="w-full sm:w-auto"
                    >
                        <span className="hidden sm:inline">+ Nova Conta Bancária</span>
                        <span className="sm:hidden">+ Nova</span>
                    </PrimaryButton>
                )}
            </div>

            {filteredData.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                    <p className="text-gray-500 text-sm sm:text-lg">
                        {searchTerm
                            ? `Nenhuma conta encontrada para "${searchTerm}".`
                            : 'Nenhuma conta bancária cadastrada.'}
                    </p>
                </div>
            ) : (
                <>
                    {/* Cards para Mobile */}
                    <div className="grid grid-cols-1 md:hidden gap-2 sm:gap-4">
                        {filteredData.map((conta) => (
                            <ContaCard
                                key={conta.id}
                                conta={conta}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                onEdit={handleOpenEditModal}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>

                    {/* Tabela para Desktop */}
                    <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Nome
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Banco
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tipo
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Saldo Atual
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredData.map((conta) => (
                                    <tr key={conta.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {conta.nome}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {conta.banco}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {conta.tipo_conta}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600">
                                            {formatCurrency(conta.saldo_atual)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {conta.ativo ? (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    Ativa
                                                </span>
                                            ) : (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                    Inativa
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {canEdit && (
                                                <span
                                                    onClick={() => handleOpenEditModal(conta)}
                                                    className="text-emerald-600 hover:text-emerald-900 cursor-pointer mr-3"
                                                >
                                                    Editar
                                                </span>
                                            )}
                                            {canDelete && (
                                                <span
                                                    onClick={() => handleDelete(conta.id)}
                                                    className="text-red-600 hover:text-red-900 cursor-pointer"
                                                >
                                                    Desativar
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default ListaContasBancarias;

